/**
 * Vòng đời `Article` — bảng ở `data-model.md` §4 dịch thành **mã kiểm được**.
 *
 * Vì sao đáng làm thành hàm thuần thay vì vài câu `if` rải trong service:
 *
 * 1. **`AP-12`: mọi trạng thái phải có bộ đếm trên màn Tổng quan.** Một trạng thái không ai đếm
 *    là một trạng thái xác sống. Cách nó xảy ra: thêm trạng thái mà quên thêm bộ đếm. Ở đây
 *    `TRANG_THAI_BAI` là **một danh sách duy nhất**, và có test bắt màn Tổng quan phải phủ hết nó.
 * 2. **Chuyển trạng thái sai là hỏng câm.** `PENDING_REVIEW → PUBLISHED_WEB` (bỏ qua cửa duyệt)
 *    không làm gì nổ cả — nó chỉ lặng lẽ đăng một bài chưa ai đọc lên website khách hàng thật.
 *    Ở đây nó **ném**.
 */
export const TRANG_THAI_BAI = [
    "BRIEF_READY",
    "WRITING",
    "NEEDS_IMAGE",
    "PENDING_REVIEW",
    "REJECTED_TEXT",
    "REJECTED_IMAGE",
    "APPROVED",
    "SCHEDULED",
    "PUBLISHED_WEB",
    "PUBLISHED_ALL",
    "FAILED",
    "DISCARDED",
];
/**
 * Chuyển trạng thái nào là hợp lệ.
 *
 * Đọc thẳng từ cột "Ai đặt / Ai đọc" của bảng `data-model.md` §4. Một cạnh không có ở đây là một
 * cạnh **không tồn tại** — không phải "chưa nghĩ tới".
 */
const CHO_PHEP = {
    BRIEF_READY: ["WRITING", "FAILED", "DISCARDED"],
    WRITING: ["NEEDS_IMAGE", "FAILED", "DISCARDED"],
    NEEDS_IMAGE: ["PENDING_REVIEW", "FAILED", "DISCARDED"],
    // Cửa duyệt. Bốn lối ra, và KHÔNG có lối nào đi thẳng tới SCHEDULED.
    PENDING_REVIEW: ["APPROVED", "REJECTED_TEXT", "REJECTED_IMAGE", "DISCARDED", "FAILED"],
    // Từ chối bài ⇒ viết lại từ đầu, nên quay về WRITING.
    REJECTED_TEXT: ["WRITING", "DISCARDED", "FAILED"],
    // Từ chối ảnh ⇒ CHỈ vẽ lại ảnh. Không viết lại bài — bài đã được duyệt về mặt chữ.
    REJECTED_IMAGE: ["NEEDS_IMAGE", "DISCARDED", "FAILED"],
    APPROVED: ["SCHEDULED", "DISCARDED", "FAILED"],
    SCHEDULED: ["PUBLISHED_WEB", "FAILED", "DISCARDED"],
    // Đã lên web rồi thì KHÔNG còn DISCARDED — bài đã ở ngoài đời, xoá bản ghi không gỡ được nó.
    PUBLISHED_WEB: ["PUBLISHED_ALL", "FAILED"],
    PUBLISHED_ALL: [],
    // FAILED không phải ngõ cụt: Lynust bấm "Chạy lại" thì bài quay về chặng trước đó.
    FAILED: ["BRIEF_READY", "WRITING", "NEEDS_IMAGE", "PENDING_REVIEW", "SCHEDULED", "DISCARDED"],
    DISCARDED: [],
};
/** Trạng thái mà bài **đang chờ người**, không chờ máy. Đây là con số của màn Hàng chờ duyệt. */
export const CHO_NGUOI = [
    "PENDING_REVIEW",
    "REJECTED_TEXT",
    "REJECTED_IMAGE",
];
/** Trạng thái mà bài **đã ra ngoài đời** — không rút lại được bằng cách sửa database. */
export const DA_RA_NGOAI = ["PUBLISHED_WEB", "PUBLISHED_ALL"];
/**
 * Trạng thái mà bài **đang nằm trong tay một job**. Kẹt ở đây quá lâu là dấu hiệu hỏng —
 * `AP-12` gọi là "trạng thái xác sống".
 */
export const DANG_CHO_MAY = ["BRIEF_READY", "WRITING", "NEEDS_IMAGE"];
/** Quá bao lâu ở `WRITING` thì coi là kẹt. `[quy ước nội bộ — chưa kiểm chứng, 08/09/2026]` */
export const NGUONG_KET_PHUT = 10;
export function laTrangThaiBai(x) {
    return typeof x === "string" && TRANG_THAI_BAI.includes(x);
}
export class ChuyenTrangThaiSai extends Error {
    tu;
    den;
    constructor(tu, den, themVao) {
        super(`Không được chuyển ${tu} → ${den}.` +
            (themVao ? ` ${themVao}` : "") +
            ` Lối ra hợp lệ của ${tu}: ${CHO_PHEP[tu].join(", ") || "(không có — đây là trạng thái cuối)"}`);
        this.name = "ChuyenTrangThaiSai";
        this.tu = tu;
        this.den = den;
    }
}
export function duocChuyen(tu, den) {
    return CHO_PHEP[tu].includes(den);
}
/**
 * Kiểm một lượt chuyển trạng thái. **Ném** nếu sai.
 *
 * Gọi hàm này **trước** khi ghi vào database, không phải sau.
 */
export function kiemChuyen(tu, den) {
    if (!laTrangThaiBai(tu))
        throw new Error(`Trạng thái nguồn lạ: ${JSON.stringify(tu)}`);
    if (!laTrangThaiBai(den))
        throw new Error(`Trạng thái đích lạ: ${JSON.stringify(den)}`);
    if (tu === den)
        throw new ChuyenTrangThaiSai(tu, den, "Trạng thái không đổi.");
    if (duocChuyen(tu, den))
        return;
    // Ba lối tắt nguy hiểm nhất được gọi tên riêng — thông điệp chung không đủ để người đọc
    // hiểu vì sao nó bị chặn.
    if (tu === "PENDING_REVIEW" && (den === "SCHEDULED" || DA_RA_NGOAI.includes(den))) {
        throw new ChuyenTrangThaiSai(tu, den, "Đây là ĐI VÒNG QUA CỬA DUYỆT. Mọi luồng đều chờ duyệt — đó là quyết định của Lynust, " +
            "không phải thiếu sót. Bài phải qua APPROVED trước.");
    }
    if (DA_RA_NGOAI.includes(tu) && den === "DISCARDED") {
        throw new ChuyenTrangThaiSai(tu, den, "Bài đã đăng ra ngoài rồi — đổi trạng thái trong database KHÔNG gỡ nó khỏi website. " +
            "Automation API của IME không có PUT/DELETE; phải vào admin IME gỡ bằng tay.");
    }
    if (tu === "REJECTED_IMAGE" && den === "WRITING") {
        throw new ChuyenTrangThaiSai(tu, den, "Từ chối ẢNH thì chỉ vẽ lại ảnh (→ NEEDS_IMAGE). Viết lại cả bài là vứt một bài đã " +
            "được duyệt về mặt chữ, và tốn thêm một lượt gọi model.");
    }
    throw new ChuyenTrangThaiSai(tu, den);
}
/**
 * Từ chối hoặc bỏ một bài thì **phải huỷ mọi job đang chờ của bài đó** (`AP-95`).
 *
 * Không huỷ thì bài vừa bị từ chối **vẫn lên tài khoản thật** — job đăng đã nằm trong hàng đợi
 * từ trước và nó không biết chuyện gì vừa xảy ra ở cửa duyệt.
 */
export function phaiHuyJobDangCho(den) {
    return den === "REJECTED_TEXT" || den === "REJECTED_IMAGE" || den === "DISCARDED";
}
/**
 * Từ chối **bắt buộc có lý do**. Đây không phải phép lịch sự với dữ liệu — lý do từ chối là
 * **đầu vào duy nhất của vòng học**, và nó **không tái tạo lại được**: bỏ qua hôm nay thì mãi
 * mãi không biết vì sao bài đó bị loại.
 *
 * **30 — Lynust chốt 09/09/2026** (`checklist.md` Q6), nâng từ 10. Lý do dài hơn ⇒ luật sinh ra
 * từ nó sắc hơn. 10 chỉ đủ chặn `"xấu"` và `"ko dc"`; 30 buộc nói **bài sai ở đâu**.
 *
 * ⚠️ `apps/web/src/trang/ChiTietBai.tsx` **chép lại con số này** vì `apps/web` không phụ thuộc
 * `@autoime/shared`. Đổi ở đây thì phải đổi cả bên đó — nếu không, dashboard đếm ký tự theo một
 * ngưỡng còn API chặn theo ngưỡng khác. Xem `checklist.md` **B10**.
 */
export const DAI_LY_DO_TOI_THIEU = 30;
export function kiemLyDoTuChoi(den, lyDo) {
    if (den !== "REJECTED_TEXT" && den !== "REJECTED_IMAGE") {
        throw new Error(`kiemLyDoTuChoi chỉ dùng cho REJECTED_*, nhận ${den}`);
    }
    const s = (lyDo ?? "").trim();
    if (s.length < DAI_LY_DO_TOI_THIEU) {
        throw new Error(`Từ chối phải kèm lý do từ ${DAI_LY_DO_TOI_THIEU} ký tự trở lên. ` +
            `Lý do là đầu vào DUY NHẤT của vòng học và KHÔNG bổ sung hồi tố được — ` +
            `"không hay" không nói được gì cho bài sau.`);
    }
    return s;
}
//# sourceMappingURL=vong-doi.js.map